import { Component, OnDestroy, ViewChild } from '@angular/core';
import { InvoiceDetailsComponent } from 'components/accounting/invoice-details.component';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { LedgerPostType } from 'enums/LedgerPostType';
import { ProgressMessageStatus } from 'enums/ProgressMessageStatus';
import { LedgerPostSearchModel } from 'models/generated/Accounting.Api.Models.LedgerPostSearch';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { InvoicingService } from 'services/invoicing.service';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { UserService } from 'services/user.service';
import { LedgerPostsComponent } from './ledger-posts.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { CompanyAccountingSettingModel } from 'models/generated/Entities.Accounting.CompanyAccountingSetting';
import { Pipes } from 'modules/pipes';
import { BrandService } from 'services/brand.service';
import { BrandModel } from 'models/generated/Entities.GormCrm.Brand';
import { Inputs } from 'modules/inputs';

@Component({
	selector: 'search-ledger-posts',
	templateUrl: './search-ledger-posts.component.html',
	providers: [FormValidationService],
	imports: [Layout, Inputs, ListView, InvoiceDetailsComponent, CompanyInformationComponent, ListContactComponent, LedgerPostsComponent, Pipes]
})
@ComponentUrl('search-ledger-posts')
@CredentialRequired(CredentialType.ExportLedgerPosts)
export class SearchLedgerPostsComponent implements OnDestroy {
	@ViewChild('searchButton') searchButton!: SubmitButtonComponent;
	@ViewChild('exportButton') exportButton!: SubmitButtonComponent;
	@ViewChild('searchLedgersList') searchLedgersList?: ListViewComponent;

	private exportProcess?: Subscription;

	ledgerPosts?: LedgerPostModel[];
	highlightedLedgerPost?: LedgerPostModel;
	maxPlannedDate?: Date;
	brandId: string;
	brands: BrandModel[];
	maxAllowedLookAhead: Date;
	selectedLedgerPosts: LedgerPostModel[] = [];
	inputTypeEnum = InputType;
	loadingBillingInfo = true;
	billingInfo?: BillingInfoDtoModel;
	loadingCompany = true;
	company?: CompanyModel;
	customerNumbers?: CompanyAccountingSettingModel[];
	loadingCustomerNo = true;

	constructor(
		private readonly invoicingService: InvoicingService,
		private readonly accountingService: AccountingService,
		private readonly productPlanService: ProductPlanService,
		private readonly userService: UserService,
		private readonly pushService: PushService,
		private readonly productName: ProductNamePipe,
		private readonly notificationService: NotificationService,
		private readonly companyService: CompanyService,
		brandService: BrandService,
		dateService: DateService)
	{
		this.maxAllowedLookAhead = dateService.addDays(new Date(), 30);
		this.brands = brandService.brands;
		this.brandId = this.brands[0].id!;
	}

	searchLedgerPosts() {
		let request = new LedgerPostSearchModel();
		request.maxPlannedDate = this.maxPlannedDate;
		request.brandId = this.brandId;
		request.types = [LedgerPostType.Invoice, LedgerPostType.StartupInvoice, LedgerPostType.SettlementInvoice, LedgerPostType.CreditMemo];
		this.selectedLedgerPosts = [];
		if (this.exportButton) this.exportButton.reset();
		this.accountingService.searchLedgerPosts(request).then(posts => {
			let productPlanIds = posts.filter(post => post.productPlanId != undefined).map(post => post.productPlanId!)
				.concat(posts.filter(post => post.accountingPlan && post.accountingPlan.productPlanId != undefined).map(post => post.accountingPlan!.productPlanId!));
			this.productPlanService.getProductPlans(productPlanIds).then(productPlans => {
				posts.forEach(post => {
					const productPlan = productPlans.find(plan => plan.id == post.accountingPlan?.productPlanId || plan.id == post.productPlanId);
					const productName = this.productName.transform(productPlan, true);
					post.productPlan = productPlan;
					post.lineDescription = productName;
					if (post.productPlanId != undefined && post.accountingPlanId == undefined)
						post.lineDescription += ' - ' + post.description;
				});
				this.ledgerPosts = posts;
				this.selectedLedgerPosts = posts.filter(post => this.selectedLedgerPosts.find(selectedPost => selectedPost.id === post.id));
				this.searchButton.reset();
			});
		});
	}

	selectAllPosts(event: MouseEvent) {
		let select = (event.target as HTMLInputElement).checked;
		if (select && this.searchLedgersList) {
			const data = this.searchLedgersList.getFilteredListData();
			if (data)
				this.selectedLedgerPosts = [...data as LedgerPostModel[]];
		}
		else this.selectedLedgerPosts = [];
		if (!this.exportButton.working) this.exportButton.reset();
	}

	selectPost(post: LedgerPostModel) {
		if (this.selectedLedgerPosts.indexOf(post) >= 0) {
			this.selectedLedgerPosts.splice(this.selectedLedgerPosts.indexOf(post), 1);
		}
		else {
			this.selectedLedgerPosts.push(post);
		}
		if (!this.exportButton.working) this.exportButton.reset();
	}

	removeSelectedPost(post: LedgerPostModel) {
		if (this.selectedLedgerPosts.indexOf(post) >= 0) {
			this.selectedLedgerPosts.splice(this.selectedLedgerPosts.indexOf(post), 1);
		}
	}

	onSelectLoadBillingDetails(post: LedgerPostModel) {
		this.loadingCompany = true;
		this.loadingBillingInfo = true;
		this.loadingCustomerNo = true;
		this.highlightedLedgerPost = post;

		let companyId = undefined;
		if (post.productPlan) {
			companyId = post.productPlan!.companyId!;
		} else if (post.accountingPlan) {
			companyId = post.accountingPlan!.companyId!;
		}

		if (companyId && companyId != this.company?.id) {
			this.companyService.getCompany(companyId).then(company => {
				this.company = company;
				this.loadingCompany = false;
			});
			this.companyService.getCompanyBillingInfo(companyId).then(billingInfo => {
				this.billingInfo = billingInfo;
				this.loadingBillingInfo = false;
			});
			this.accountingService.getCompanySettings(companyId).then(settings => {
				this.customerNumbers = settings.filter(s => s.customerNo);
				this.loadingCustomerNo = false;
			})	
		} else {
			this.loadingCompany = false;
			this.loadingBillingInfo = false;
			this.loadingCustomerNo = false;
		}
	}

	exportLedgerPosts() {

		const selectedPosts = [...this.selectedLedgerPosts]; // Remember selected posts on button press
		const currentUserId = this.userService.getCurrentUser().id!;

		if (this.exportProcess) this.exportProcess.unsubscribe();
		let exportProcessProgression = 0;
		this.exportProcess = this.pushService.onProgressChanges(currentUserId).subscribe(event => {
			exportProcessProgression += event.processed!;
			if (this.exportButton.progression) {
				let exportProcessTotal = this.exportButton.progression.total!;
				this.exportButton.progression = { processed: exportProcessProgression, total: exportProcessTotal };
				if (exportProcessTotal == exportProcessProgression) {
					this.exportButton.succeeded();
				}
			}
			if (event.status == ProgressMessageStatus.Error) {
				this.notificationService.error('search-ledger-posts.error-exporting-ledger-posts', '');
				this.exportButton.reset();
			}
		});

		this.invoicingService.exportLedgerPosts(selectedPosts.map(post => { return { postId: post.id!, description: post.lineDescription }; })).then(progressEvent => {
			selectedPosts.forEach(post => post.ledgerHeaderId = '00000000-0000-0000-0000-000000000000');
			this.selectedLedgerPosts = [];
			this.exportButton.progression = { processed: 0, total: progressEvent.total };
		}, _ => this.exportButton.reset());
	}

	ngOnDestroy(): void {
		if (this.exportProcess) this.exportProcess.unsubscribe();
	}
	
}
